import React, { useCallback, useState } from 'react';
import { Card, Row, Col, ProgressBar, Alert } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';

import usePolling from 'hooks/usePolling';

import { picklistApi } from 'api';
import paths from 'routes/paths';

const POLLING_INTERVAL_MS = 10000;

/**
 * Batch-mode picklist live picking panel for the PC detail page.
 * Shows the QR code that opens the mobile picking page (receiving status
 * only) and polls picking progress while the page is visible.
 */
const LivePickingCard = ({ picklistId, picklistStatus, tenant, warehouse }) => {
  const [state, setState] = useState(null);

  const isReceiving = picklistStatus === 'receiving';

  const mobilePath = paths.picklists.livePicking(tenant, warehouse, picklistId);
  const mobileUrl = `${window.location.origin}${mobilePath}`;

  const fetchState = useCallback(async () => {
    try {
      const response = await picklistApi.getLivePickingState(picklistId);
      setState(response);
    } catch (error) {
      // Progress polling failures are non-fatal on the detail page.
    }
  }, [picklistId]);

  usePolling(fetchState, POLLING_INTERVAL_MS, isReceiving);

  React.useEffect(() => {
    if (isReceiving) {
      fetchState();
    }
  }, [isReceiving, fetchState]);

  const progress = state?.progress;
  const total = progress?.total_units || 0;
  const picked = progress?.picked || 0;
  const skipped = progress?.skipped || 0;
  const done = picked + skipped;
  const isComplete = total > 0 && progress?.remaining === 0;

  return (
    <Card className="mb-3">
      <Card.Header className="bg-light py-2">
        <span className="fw-semibold">Batch Picking</span>
      </Card.Header>
      <Card.Body className="p-3">
        {isReceiving ? (
          <>
            <div className="d-flex flex-column align-items-center mb-3">
              <QRCodeSVG value={mobileUrl} size={132} marginSize={2} />
              <a
                href={mobileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-monospace fs-11 mt-2 text-center text-break"
              >
                {mobileUrl}
              </a>
              <span className="text-muted fs-10 mt-1 text-center">
                Scan with the picker&apos;s phone to start batch picking
              </span>
            </div>

            {progress && (
              <>
                <Row className="g-2 mb-2">
                  <Col xs={6} className="text-muted fs-10">
                    Progress
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {done} / {total} units
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Picked / Skipped
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {picked} / {skipped}
                  </Col>

                  <Col xs={6} className="text-muted fs-10">
                    Partitions
                  </Col>
                  <Col xs={6} className="text-end fw-medium fs-10">
                    {progress.total_partitions}
                  </Col>
                </Row>

                <ProgressBar style={{ height: 8 }} className="mb-2">
                  <ProgressBar
                    variant="success"
                    now={total ? (picked / total) * 100 : 0}
                    key={1}
                  />
                  <ProgressBar
                    variant="warning"
                    now={total ? (skipped / total) * 100 : 0}
                    key={2}
                  />
                </ProgressBar>

                {isComplete && (
                  <Alert variant="success" className="fs-10 py-2 mb-0">
                    Batch picking complete — ready for packing.
                    {skipped > 0 &&
                      ` ${skipped} skipped unit(s) must be rejected at packing.`}
                  </Alert>
                )}

                {progress.unassigned_units > 0 && (
                  <Alert variant="warning" className="fs-10 py-2 mb-0 mt-2">
                    {progress.unassigned_units} unit(s) have no allocated batch
                    and are excluded from batch picking.
                  </Alert>
                )}
              </>
            )}
          </>
        ) : (
          <span className="text-muted fs-10">
            {picklistStatus === 'created'
              ? 'Receive the picklist to start batch picking.'
              : 'This picklist is closed.'}
          </span>
        )}
      </Card.Body>
    </Card>
  );
};

export default LivePickingCard;
